/**
 * Unified Enrichment Wizard
 * 
 * Single-page wizard combining product selection, AI enrichment, and approval.
 * Features:
 * - Spreadsheet-like grid with real-time cell updates
 * - Filters: Not Enriched / Processing / Enriched / All
 * - Mode chips for selecting enrichment types
 * - Inline approval and push actions
 * - Confirmation modal before enrichment
 * - Progress tracking during processing
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db } from 'config/firebase.js';
import {
    collection, query, getDocs, where, limit,
    onSnapshot, orderBy, doc, updateDoc
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { Badge, Spinner, ProgressBar, Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck, faSpinner, faClock, faRocket,
    faSort, faSortUp, faSortDown, faExclamationTriangle,
    faTimes, faFilter, faCog
} from '@fortawesome/free-solid-svg-icons';
import missingImage from 'assets/merlin/missing_image.png';
import { getProductImage } from '../../../utils/getProductImage';
import './UnifiedEnrichmentWizard.css';
import './animations.css';
import PushStatusBadge from './PushStatusBadge';
import CostAccumulator from './CostAccumulator';
import BudgetSettingsModal from './BudgetSettingsModal';
import { useBudgetSettings } from './hooks/useBudgetSettings';
import { useFirebaseAuth } from '../../../context/FirebaseAuthContext';

const CLOUD_FUNCTIONS_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

// Mode configuration
const MODE_CONFIG = {
    metadata: { cost: 0.015, label: 'Metadata', icon: '📋' },
    title: { cost: 0.008, label: 'Title', icon: '✏️' },
    description: { cost: 0.012, label: 'Description', icon: '📝' },
    seo: { cost: 0.020, label: 'SEO Tags', icon: '🏷️' },
    pricing: { cost: 0.005, label: 'Pricing', icon: '💰' },
    weight: { cost: 0.010, label: 'Weight', icon: '⚖️' }
};

const ALL_MODES = ['metadata', 'title', 'description', 'seo', 'pricing', 'weight'];
const BATCH_SIZE_LIMIT = 100;
const PAGE_SIZE = 50;

// Enrichment status filter options
const STATUS_FILTERS = {
    all: { label: 'All Products', color: '#374151' },
    not_enriched: { label: 'Not Enriched', color: '#6B7280' },
    processing: { label: 'Processing', color: '#3B82F6' },
    completed: { label: 'Needs Review', color: '#F59E0B' },
    approved: { label: 'Approved', color: '#10B981' }
};

// Success Checkmark Component for visual feedback
const SuccessCheckmark = ({ size = 24, className = '' }) => (
    <svg
        className={`success-checkmark ${className}`}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M5 12l5 5L19 7" />
    </svg>
);

const UnifiedEnrichmentWizard = () => {
    // Firebase auth
    const [user] = useFirebaseAuth();

    // Budget settings
    const { budget, loading: budgetLoading, saveBudget } = useBudgetSettings(user?.uid);
    const [showBudgetSettings, setShowBudgetSettings] = useState(false);

    // Product data state
    const [products, setProducts] = useState([]);
    const [enrichmentJobs, setEnrichmentJobs] = useState({});
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    // Selection state
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [selectedModes, setSelectedModes] = useState(['title', 'description']);

    // SEO Protection bypass - allows overriding protection for products already in Shopify
    const [bypassSeoProtection, setBypassSeoProtection] = useState(false);

    // Filter state - Default to 'all' for better first-time experience
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [shopifyFilter, setShopifyFilter] = useState('all');

    // Pagination & sorting
    const [currentPage, setCurrentPage] = useState(0);
    const [sortColumn, setSortColumn] = useState('sku');
    const [sortDirection, setSortDirection] = useState('asc');

    // Processing state
    const [processing, setProcessing] = useState(false);
    const [activeJobIds, setActiveJobIds] = useState(new Set());
    // Use array instead of Set for proper React change detection
    const [pendingSkuList, setPendingSkuList] = useState([]);

    // Progress tracking
    const [enrichmentProgress, setEnrichmentProgress] = useState({ total: 0, completed: 0 });

    // Cost tracking
    const [batchCost, setBatchCost] = useState({ total: 0, byJob: {} });

    // Force re-render counter for pending state changes
    const [, forceUpdate] = useState(0);

    // Confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Cells that were recently updated (for animation)
    const [updatedCells, setUpdatedCells] = useState(new Set());

    // Success feedback state
    const [showSuccess, setShowSuccess] = useState(false);

    // Ref to track previous jobs for animation detection
    const previousJobsRef = useRef({});

    // Track when enrichment was started to only clear pending for NEW jobs
    const enrichmentStartTimeRef = useRef(null);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Load products from search endpoint
    useEffect(() => {
        const loadProducts = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    q: debouncedQuery,
                    searchMode: 'keywords',
                    shopify: shopifyFilter,
                    limit: '200',
                    offset: '0'
                });

                const response = await fetch(`${CLOUD_FUNCTIONS_URL}/productSearch?${params}`);
                if (!response.ok) throw new Error('Search failed');

                const result = await response.json();
                const productsWithIds = (result.products || []).map(p => ({
                    ...p,
                    id: p.id || p.group_id || p.sku,
                    thumbnail: getProductImage(p)
                }));
                setProducts(productsWithIds);
                setTotalCount(result.total || 0);
                setCurrentPage(0);
            } catch (error) {
                console.error('Error loading products:', error);
                toast.error('Failed to load products');
            } finally {
                setLoading(false);
            }
        };

        loadProducts();
    }, [debouncedQuery, shopifyFilter]);

    // Real-time listener for enrichment jobs - BATCHED to handle >30 SKUs
    useEffect(() => {
        if (products.length === 0) return;

        const skus = products.map(p => p.sku).filter(Boolean);
        if (skus.length === 0) return;

        // Batch SKUs into groups of 30 (Firestore 'in' query limit)
        const skuBatches = [];
        for (let i = 0; i < skus.length; i += 30) {
            skuBatches.push(skus.slice(i, i + 30));
        }

        const unsubscribers = [];
        const allJobs = {};

        skuBatches.forEach((batch, batchIndex) => {
            const jobsQuery = query(
                collection(db, 'productCatalog/topicz/enrichmentJobs'),
                where('sku', 'in', batch),
                orderBy('createdAt', 'desc')
            );

            const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
                console.log(`[EnrichmentJobs] Received ${snapshot.docs.length} jobs for batch ${batchIndex}`);
                snapshot.docs.forEach(docSnap => {
                    const job = { id: docSnap.id, ...docSnap.data() };
                    // Keep latest job per SKU
                    if (!allJobs[job.sku] || job.createdAt > allJobs[job.sku].createdAt) {
                        allJobs[job.sku] = job;
                    }

                    // Detect cell updates for animation
                    const prevJob = previousJobsRef.current[job.sku];
                    if (prevJob) {
                        if (JSON.stringify(prevJob.results) !== JSON.stringify(job.results)) {
                            setUpdatedCells(prev => new Set([...prev, `${job.sku}-results`]));
                            setTimeout(() => {
                                setUpdatedCells(prev => {
                                    const next = new Set(prev);
                                    next.delete(`${job.sku}-results`);
                                    return next;
                                });
                            }, 2000);
                        }
                    }

                    // Only clear from pending if this job is COMPLETED (not just found)
                    // This keeps the row showing as "processing" until enrichment actually finishes
                    if (pendingSkuList.includes(job.sku)) {
                        const isComplete = job.status === 'completed' || job.status === 'approved' ||
                            job.status === 'partial' || job.status === 'failed';

                        if (isComplete) {
                            console.log(`✅ Job ${job.sku} completed with status: ${job.status}`);
                            setPendingSkuList(prev => prev.filter(s => s !== job.sku));
                        } else {
                            // Job exists but still processing - log progress
                            console.log(`⏳ Job ${job.sku} status: ${job.status}, mode: ${job.progress?.current || 'starting'}`);
                        }
                    }
                });

                // Update jobs ref for animation detection
                previousJobsRef.current = { ...allJobs };
                setEnrichmentJobs({ ...allJobs });

                // Calculate running cost for active jobs
                if (activeJobIds.size > 0 || pendingSkuList.length > 0) {
                    const sessionJobs = Object.values(allJobs).filter(j =>
                        activeJobIds.has(j.id) || pendingSkuList.includes(j.sku)
                    );

                    let totalCost = 0;
                    const costByJob = {};

                    sessionJobs.forEach(job => {
                        let jobCost = 0;

                        // Extract cost from job.results.{mode}.usage.cost_usd
                        if (job.results) {
                            Object.keys(job.results).forEach(mode => {
                                const modeResult = job.results[mode];
                                if (modeResult?.usage?.cost_usd) {
                                    jobCost += modeResult.usage.cost_usd;
                                }
                            });
                        }

                        if (jobCost > 0) {
                            costByJob[job.id] = jobCost;
                            totalCost += jobCost;
                        }
                    });

                    setBatchCost({ total: totalCost, byJob: costByJob });
                }

                // FIXED: Only track progress for jobs from the CURRENT enrichment session
                // Use activeJobIds and pendingSkuList to track only what we started
                if (activeJobIds.size > 0 || pendingSkuList.length > 0) {
                    // Get jobs that are part of the current session
                    const sessionJobs = Object.values(allJobs).filter(j =>
                        activeJobIds.has(j.id) || pendingSkuList.includes(j.sku)
                    );

                    // Count completed jobs in the current session
                    const completedCount = sessionJobs.filter(j =>
                        j.status === 'completed' || j.status === 'approved' ||
                        j.status === 'partial' || j.status === 'failed'
                    ).length;

                    // Total is the number of jobs we started (not all jobs)
                    const totalInSession = Math.max(pendingSkuList.length, activeJobIds.size, sessionJobs.length);

                    setEnrichmentProgress({
                        total: totalInSession,
                        completed: completedCount
                    });

                    // Auto-clear progress when all jobs are done
                    if (completedCount >= totalInSession && totalInSession > 0) {
                        console.log('✅ All jobs completed! Clearing progress bar.');
                        // Delay slightly so user sees 100% before it disappears
                        setTimeout(() => {
                            setEnrichmentProgress({ total: 0, completed: 0 });
                            setActiveJobIds(new Set());
                            setPendingSkuList([]);
                            setBatchCost({ total: 0, byJob: {} });
                        }, 2000);
                    }
                }
            }, (error) => {
                // Error callback for onSnapshot - this catches missing index errors
                console.error(`[EnrichmentJobs] Query error for batch ${batchIndex}:`, error.message);
                if (error.message.includes('index')) {
                    toast.error('Database index missing. Please deploy Firestore indexes: firebase deploy --only firestore:indexes');
                }
            });

            unsubscribers.push(unsubscribe);
        });

        return () => unsubscribers.forEach(unsub => unsub());
    }, [products, activeJobIds, pendingSkuList]);

    // Merge products with their enrichment jobs
    const mergedData = useMemo(() => {
        // Debug log
        if (pendingSkuList.length > 0) {
            console.log('📋 Pending SKU list:', pendingSkuList);
        }

        return products.map(product => {
            const job = enrichmentJobs[product.sku] || null;
            const isPending = pendingSkuList.includes(product.sku);

            // Determine actual status - consider both local pending and Firestore job status
            let enrichmentStatus = 'not_enriched';
            if (isPending) {
                enrichmentStatus = 'pending';
            } else if (job?.status === 'pending' || job?.status === 'processing') {
                enrichmentStatus = 'processing';
            } else if (job?.status) {
                enrichmentStatus = job.status;
            }

            // Debug: log if a product is marked as pending or processing
            if (isPending || job?.status === 'processing') {
                console.log(`✨ Product ${product.sku} status: ${enrichmentStatus}, mode: ${job?.progress?.current || 'waiting'}`);
            }

            return {
                ...product,
                job,
                enrichmentStatus,
                aiTitle: job?.results?.title?.data?.optimized_title || null,
                aiDescription: job?.results?.description?.data?.body_html ||
                    job?.results?.description?.data?.html_description || null,
                processingMode: job?.progress?.current || (isPending ? 'Queued' : null),
                isPending,
                // Include mode completion info
                completedModes: job?.progress?.completed || 0,
                totalModes: job?.modes?.length || 0
            };
        });
    }, [products, enrichmentJobs, pendingSkuList]);

    // Apply status filter
    const filteredData = useMemo(() => {
        let result = [...mergedData];

        if (statusFilter !== 'all') {
            result = result.filter(item => {
                if (statusFilter === 'not_enriched') {
                    return !item.job && !item.isPending;
                }
                if (statusFilter === 'processing') {
                    return item.enrichmentStatus === 'processing' || item.isPending;
                }
                return item.enrichmentStatus === statusFilter;
            });
        }

        // Sort
        result.sort((a, b) => {
            let aVal = a[sortColumn] || '';
            let bVal = b[sortColumn] || '';
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [mergedData, statusFilter, sortColumn, sortDirection]);

    // Paginate
    const paginatedData = useMemo(() => {
        const start = currentPage * PAGE_SIZE;
        return filteredData.slice(start, start + PAGE_SIZE);
    }, [filteredData, currentPage]);

    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);

    // Check if header checkbox should be indeterminate
    const headerCheckState = useMemo(() => {
        if (paginatedData.length === 0) return { checked: false, indeterminate: false };
        const selectedCount = paginatedData.filter(p => selectedProducts.has(p.id)).length;
        if (selectedCount === 0) return { checked: false, indeterminate: false };
        if (selectedCount === paginatedData.length) return { checked: true, indeterminate: false };
        return { checked: false, indeterminate: true };
    }, [paginatedData, selectedProducts]);

    // Selection handlers
    const toggleProduct = (productId) => {
        setSelectedProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                if (newSet.size >= BATCH_SIZE_LIMIT) {
                    toast.warning(`Maximum ${BATCH_SIZE_LIMIT} products per batch`);
                    return prev;
                }
                newSet.add(productId);
            }
            return newSet;
        });
    };

    const toggleAllVisible = () => {
        const allSelected = paginatedData.every(p => selectedProducts.has(p.id));
        if (allSelected) {
            setSelectedProducts(prev => {
                const newSet = new Set(prev);
                paginatedData.forEach(p => newSet.delete(p.id));
                return newSet;
            });
        } else {
            setSelectedProducts(prev => {
                const newSet = new Set(prev);
                paginatedData.forEach(p => {
                    if (newSet.size < BATCH_SIZE_LIMIT) newSet.add(p.id);
                });
                return newSet;
            });
        }
    };

    const toggleMode = (mode) => {
        setSelectedModes(prev =>
            prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]
        );
    };

    // Show confirmation modal
    const handleEnrichmentClick = () => {
        if (selectedProducts.size === 0 || selectedModes.length === 0) {
            toast.warning('Select products and modes first');
            return;
        }
        setShowConfirmModal(true);
    };

    // Start enrichment (after confirmation)
    const handleStartEnrichment = async () => {
        setShowConfirmModal(false);

        // Budget check before starting enrichment
        if (budget?.alertEnabled && budget?.threshold > 0 && costEstimate.totalCost > budget.threshold) {
            const confirmOverBudget = window.confirm(
                `Estimated cost ($${costEstimate.totalCost.toFixed(2)}) exceeds your budget threshold ($${budget.threshold.toFixed(2)}). Continue anyway?`
            );
            if (!confirmOverBudget) {
                return;
            }
        }

        setProcessing(true);

        try {
            const selectedSkus = [...selectedProducts]
                .map(id => products.find(p => p.id === id)?.sku)
                .filter(Boolean);

            console.log('🚀 Starting enrichment for SKUs:', selectedSkus);

            // Track when we started enrichment (to know which jobs are new)
            enrichmentStartTimeRef.current = new Date();

            // Immediately mark these SKUs as pending for visual feedback
            setPendingSkuList([...selectedSkus]); // Spread to ensure new array reference
            setEnrichmentProgress({ total: selectedSkus.length, completed: 0 });

            // Clear selection immediately so user sees the pending state
            setSelectedProducts(new Set());

            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/enrichmentTrigger`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    skus: selectedSkus,
                    // Use force: true to re-enrich even if already enriched
                    // Use bypassSeoProtection to allow title/description enrichment for products already in Shopify
                    options: {
                        modes: selectedModes,
                        force: true,
                        config: bypassSeoProtection ? { bypassSeoProtection: true } : {}
                    }
                })
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to start enrichment');
            }

            const result = await response.json();
            console.log('✅ Enrichment API response:', result);

            // Track active jobs for UI feedback
            if (result.jobIds && result.jobIds.length > 0) {
                setActiveJobIds(new Set(result.jobIds));

                // IMPORTANT: Trigger actual processing since Firestore triggers don't run locally
                // This calls the enrichmentProcess endpoint which runs the Claude AI worker
                console.log('🔄 Triggering enrichmentProcess for jobs:', result.jobIds);

                // Note: Firestore trigger (enrichmentAutoProcess) will also process these jobs.
                // The manual call here is a backup in case triggers don't fire (e.g., local dev).
                // The worker handles idempotency by skipping already-completed modes.
                console.log('🔄 Jobs created. Firestore trigger should process them automatically.');
                console.log('   Job IDs:', result.jobIds);
                console.log('   SKUs queued:', selectedSkus);

                // Optional: manually trigger processing as a backup
                // This is redundant with the Firestore trigger but ensures processing happens
                for (const jobId of result.jobIds) {
                    try {
                        console.log(`📤 Sending process request for job ${jobId}...`);
                        const processResponse = await fetch(`${CLOUD_FUNCTIONS_URL}/enrichmentProcess`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ jobId })
                        });

                        if (processResponse.ok) {
                            const processResult = await processResponse.json();
                            console.log(`✅ Processed job ${jobId}:`, {
                                status: processResult.results?.[0]?.result?.status,
                                modes: Object.keys(processResult.results?.[0]?.result?.results || {})
                            });
                        } else {
                            const errorText = await processResponse.text();
                            console.error(`❌ Failed to process job ${jobId}: ${processResponse.status} - ${errorText}`);
                            toast.error(`Job ${jobId} failed: ${errorText.substring(0, 100)}`);
                        }
                    } catch (processError) {
                        console.error(`❌ Error processing job ${jobId}:`, processError);
                        toast.error(`Processing error: ${processError.message}`);
                    }
                }
            }

            // Show appropriate message based on result
            if (result.skipped > 0 && result.queued === 0) {
                toast.info(`All ${result.skipped} products were skipped (already enriched or excluded)`);
                setPendingSkuList([]); // Clear pending since nothing was queued
            } else {
                toast.success(`🚀 Started enrichment for ${result.queued} products! Processing with AI...`);
            }
        } catch (error) {
            console.error('❌ Enrichment error:', error);
            toast.error(error.message);
            // Clear ALL progress state on error
            setPendingSkuList([]);
            setEnrichmentProgress({ total: 0, completed: 0 });
            setActiveJobIds(new Set());
            setBatchCost({ total: 0, byJob: {} });
        } finally {
            setProcessing(false);
        }
    };

    // Approve selected
    const handleApprove = async () => {
        const selectedItems = [...selectedProducts]
            .map(id => mergedData.find(p => p.id === id))
            .filter(item => item?.job);

        if (selectedItems.length === 0) {
            toast.warning('No enriched items selected');
            return;
        }

        setProcessing(true);
        try {
            const promises = selectedItems.map(async (item) => {
                const jobRef = doc(db, 'productCatalog/topicz/enrichmentJobs', item.job.id);
                await updateDoc(jobRef, {
                    status: 'approved',
                    approvedAt: new Date()
                });
            });

            await Promise.all(promises);

            // Show success animation
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 1500);

            toast.success(`✓ Approved ${selectedItems.length} items`);
            setSelectedProducts(new Set());
        } catch (error) {
            toast.error('Failed to approve: ' + error.message);
        } finally {
            setProcessing(false);
        }
    };

    // Push to Shopify
    const handlePushToShopify = async () => {
        const approvedItems = [...selectedProducts]
            .map(id => mergedData.find(p => p.id === id))
            .filter(item => item?.job?.status === 'approved');

        if (approvedItems.length === 0) {
            toast.warning('Select approved items to push');
            return;
        }

        if (!window.confirm(`Push ${approvedItems.length} products to Shopify?`)) {
            return;
        }

        setProcessing(true);
        let successCount = 0;

        for (const item of approvedItems) {
            try {
                const response = await fetch(`${CLOUD_FUNCTIONS_URL}/enrichmentPush`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ jobId: item.job.id })
                });

                if (response.ok) {
                    successCount++;
                }
            } catch (error) {
                console.error(`Failed to push ${item.sku}:`, error);
            }
        }

        toast.success(`🛒 Pushed ${successCount}/${approvedItems.length} to Shopify`);
        setSelectedProducts(new Set());
        setProcessing(false);
    };

    // Sorting
    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn !== column) return <FontAwesomeIcon icon={faSort} className="text-muted opacity-25" />;
        return <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortUp : faSortDown} />;
    };

    // Clear all filters
    const clearFilters = () => {
        setStatusFilter('all');
        setShopifyFilter('all');
        setSearchQuery('');
    };

    // Cost estimate
    const costEstimate = useMemo(() => {
        const productCount = selectedProducts.size;
        const totalCost = selectedModes.reduce((acc, mode) => {
            return acc + (MODE_CONFIG[mode]?.cost || 0) * productCount;
        }, 0);
        return { productCount, totalCost, timeMinutes: Math.ceil(productCount * selectedModes.length * 2 / 60) };
    }, [selectedProducts, selectedModes]);

    // Check if estimated cost exceeds budget
    const isOverBudget = budget?.alertEnabled && budget?.threshold > 0 && costEstimate.totalCost > budget.threshold;

    // Render status badge
    const renderStatusBadge = (item) => {
        const isProcessing = item.enrichmentStatus === 'processing' ||
            item.enrichmentStatus === 'pending' ||
            item.isPending;
        const isUpdating = updatedCells.has(`${item.sku}-results`);

        if (isProcessing) {
            // Format the processing mode nicely
            let modeLabel = 'Starting...';
            if (item.processingMode) {
                const modeNames = {
                    'title': '📝 Title',
                    'description': '📄 Description',
                    'metadata': '🏷️ Metadata',
                    'tagging': '🔖 Tags',
                    'seo': '🔍 SEO',
                    'weight': '⚖️ Weight',
                    'pricing': '💰 Pricing',
                    'Queued': '⏳ Queued'
                };
                modeLabel = modeNames[item.processingMode] || item.processingMode;
            }

            // Show mode progress if available
            const progressText = item.totalModes > 0
                ? ` (${item.completedModes}/${item.totalModes})`
                : '';

            return (
                <div className="status-processing">
                    <Spinner animation="border" size="sm" />
                    <span>{modeLabel}{progressText}</span>
                </div>
            );
        }

        const statusConfig = {
            not_enriched: { bg: 'secondary', label: 'Not Started' },
            pending: { bg: 'info', label: 'Queued' },
            completed: { bg: 'warning', label: 'Needs Review' },
            partial: { bg: 'warning', label: '⚠️ Partial' },
            approved: { bg: 'success', label: 'Approved' },
            pushed: { bg: 'primary', label: 'Pushed' },
            failed: { bg: 'danger', label: 'Failed' }
        };

        const config = statusConfig[item.enrichmentStatus] || statusConfig.not_enriched;

        // Show push status alongside enrichment status when applicable
        // This displays the full lifecycle: Approved -> Pushed
        const hasPushStatus = item.job?.pushResult || item.job?.status === 'pushing' || item.job?.status === 'pushed';

        // Show success checkmark animation when status changes to approved/pushed
        const showSuccessCheckmark = isUpdating &&
            (item.enrichmentStatus === 'approved' || item.enrichmentStatus === 'pushed');

        return (
            <div className="status-badges-container">
                <Badge bg={config.bg} className={isUpdating ? 'badge-pulse fade-in' : ''}>
                    {config.label}
                </Badge>
                {showSuccessCheckmark && (
                    <SuccessCheckmark size={16} className="text-success ms-1" />
                )}
                {hasPushStatus && (
                    <PushStatusBadge job={item.job} compact />
                )}
            </div>
        );
    };

    // Render AI content cell with animation
    const renderAICell = (item, field, value) => {
        const isUpdating = updatedCells.has(`${item.sku}-results`);
        const isProcessing = item.enrichmentStatus === 'processing' || item.isPending;

        if (isProcessing && !value) {
            return (
                <div className="cell-processing">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="text-muted">Generating...</span>
                </div>
            );
        }

        if (!value) {
            return <span className="text-muted">—</span>;
        }

        // Strip HTML for display
        const displayValue = value.replace(/<[^>]*>?/gm, '').slice(0, 80);

        return (
            <div className={`ai-cell ${isUpdating ? 'cell-updated' : ''}`}>
                <span title={value}>{displayValue}{value.length > 80 ? '...' : ''}</span>
                {isUpdating && <SuccessCheckmark size={14} className="text-success ms-2" />}
            </div>
        );
    };

    // Check if any filters are active
    const hasActiveFilters = statusFilter !== 'all' || shopifyFilter !== 'all' || searchQuery !== '';

    if (loading && products.length === 0) {
        return (
            <div className="wizard-loading">
                <Spinner animation="border" variant="primary" />
                <p>Loading products...</p>
            </div>
        );
    }

    return (
        <div className="unified-wizard">
            {/* Progress Bar (shows during enrichment) */}
            {(pendingSkuList.length > 0 || enrichmentProgress.total > 0) && enrichmentProgress.completed < enrichmentProgress.total && (
                <div className="wizard-progress">
                    <div className="progress-header">
                        <span>🔄 Processing enrichment...</span>
                        <span>{enrichmentProgress.completed} / {enrichmentProgress.total} completed</span>
                    </div>
                    <ProgressBar
                        now={(enrichmentProgress.completed / enrichmentProgress.total) * 100}
                        animated
                        striped
                        variant="success"
                    />
                </div>
            )}

            {/* Success completion message */}
            {enrichmentProgress.total > 0 && enrichmentProgress.completed === enrichmentProgress.total && (
                <div className="wizard-progress complete fade-in">
                    <div className="progress-header">
                        <span className="d-flex align-items-center gap-2">
                            <SuccessCheckmark size={20} className="text-success" />
                            Enrichment complete!
                        </span>
                        <span>{enrichmentProgress.completed} / {enrichmentProgress.total} completed</span>
                    </div>
                    <ProgressBar
                        now={100}
                        variant="success"
                    />
                </div>
            )}

            {/* Cost Accumulator (shows during enrichment) */}
            <CostAccumulator
                batchCost={batchCost}
                budgetThreshold={budget?.threshold}
                processing={processing || pendingSkuList.length > 0}
            />

            {/* Header */}
            <div className="wizard-header">
                <div>
                    <h2>AI Enrichment</h2>
                    <span className="wizard-subtitle">
                        {totalCount.toLocaleString()} products • {filteredData.length} shown • {selectedProducts.size} selected
                    </span>
                </div>
                <div className="wizard-actions">
                    <button
                        className="btn btn-outline-secondary btn-sm me-2"
                        onClick={() => setShowBudgetSettings(true)}
                        title="Budget Settings"
                    >
                        <FontAwesomeIcon icon={faCog} />
                    </button>
                    {selectedProducts.size > 0 && costEstimate.totalCost > 0 && (
                        <span className="cost-hint">${costEstimate.totalCost.toFixed(2)} est.</span>
                    )}
                    <button
                        className="btn btn-success"
                        onClick={handleEnrichmentClick}
                        disabled={processing || selectedProducts.size === 0 || selectedModes.length === 0}
                    >
                        {processing ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faRocket} />}
                        {' '}Start Enrichment {selectedProducts.size > 0 && `(${selectedProducts.size})`}
                    </button>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="wizard-filters">
                <div className="filters-left">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="🔍 Search SKU, title..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <select
                        className="filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {Object.entries(STATUS_FILTERS).map(([key, config]) => (
                            <option key={key} value={key}>{config.label}</option>
                        ))}
                    </select>
                    <select
                        className="filter-select"
                        value={shopifyFilter}
                        onChange={(e) => setShopifyFilter(e.target.value)}
                    >
                        <option value="all">All Shopify Status</option>
                        <option value="not_in_shopify">Not in Shopify</option>
                        <option value="in_shopify">In Shopify</option>
                    </select>
                    {hasActiveFilters && (
                        <button className="btn btn-link btn-sm text-muted" onClick={clearFilters}>
                            <FontAwesomeIcon icon={faTimes} /> Clear Filters
                        </button>
                    )}
                </div>
                <div className="filters-right">
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setSelectedProducts(new Set())}>
                        Clear Selection
                    </button>
                </div>
            </div>

            {/* Mode Selection */}
            <div className="wizard-modes">
                <span className="modes-label">Enrichment Modes:</span>
                <div className="mode-chips">
                    {ALL_MODES.map(mode => (
                        <button
                            key={mode}
                            className={`mode-chip ${selectedModes.includes(mode) ? 'selected' : ''}`}
                            onClick={() => toggleMode(mode)}
                        >
                            <span>{MODE_CONFIG[mode].icon}</span>
                            <span>{MODE_CONFIG[mode].label}</span>
                            <small>${MODE_CONFIG[mode].cost}</small>
                        </button>
                    ))}
                </div>
                {/* SEO Protection Bypass Toggle */}
                {(selectedModes.includes('title') || selectedModes.includes('description') || selectedModes.includes('seo')) && (
                    <div className="seo-bypass-toggle">
                        <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={bypassSeoProtection}
                                onChange={(e) => setBypassSeoProtection(e.target.checked)}
                            />
                            <span>Override SEO protection for products already in Shopify</span>
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="seo-bypass-tooltip">
                                        Products already in Shopify normally skip Title, Description, and SEO modes to protect existing search rankings.
                                        Enable this to force enrichment of these fields anyway.
                                    </Tooltip>
                                }
                            >
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" style={{ cursor: 'help' }} />
                            </OverlayTrigger>
                        </label>
                    </div>
                )}
            </div>

            {/* Selection Summary */}
            {selectedProducts.size > 0 && (
                <div className="wizard-summary">
                    <span><strong>{selectedProducts.size}</strong> products × <strong>{selectedModes.length}</strong> modes</span>
                    <span>Est. cost: <strong>${costEstimate.totalCost.toFixed(2)}</strong></span>
                    <span>Est. time: <strong>~{costEstimate.timeMinutes} min</strong></span>
                    <div className="summary-actions">
                        <button
                            className="btn btn-outline-success btn-sm"
                            onClick={handleApprove}
                            disabled={processing}
                        >
                            {processing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Approving...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCheck} /> Approve Selected
                                </>
                            )}
                        </button>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={handlePushToShopify}
                            disabled={processing}
                        >
                            {processing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Pushing...
                                </>
                            ) : (
                                <>
                                    🛒 Push to Shopify
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Spreadsheet Grid */}
            <div className="wizard-grid-container">
                <table className="wizard-grid">
                    <thead>
                        <tr>
                            <th className="col-checkbox">
                                <input
                                    type="checkbox"
                                    checked={headerCheckState.checked}
                                    ref={el => {
                                        if (el) el.indeterminate = headerCheckState.indeterminate;
                                    }}
                                    onChange={toggleAllVisible}
                                />
                            </th>
                            <th className="col-image">Image</th>
                            <th className="col-sku sortable" onClick={() => handleSort('sku')}>
                                SKU {getSortIcon('sku')}
                            </th>
                            <th className="col-title sortable" onClick={() => handleSort('title')}>
                                Original Title {getSortIcon('title')}
                            </th>
                            <th className="col-ai-title">AI Title</th>
                            <th className="col-ai-desc">AI Description</th>
                            <th className="col-status">Status</th>
                            <th className="col-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? paginatedData.map(item => (
                            <tr
                                key={item.id}
                                className={`
                                    ${selectedProducts.has(item.id) ? 'selected' : ''}
                                    ${item.enrichmentStatus === 'processing' || item.enrichmentStatus === 'pending' || item.isPending ? 'row-processing' : ''}
                                    ${updatedCells.has(`${item.sku}-results`) ? 'row-updated' : ''}
                                `}
                                onClick={() => toggleProduct(item.id)}
                            >
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedProducts.has(item.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => toggleProduct(item.id)}
                                    />
                                </td>
                                <td className="col-image-cell">
                                    <img
                                        src={item.thumbnail || missingImage}
                                        alt=""
                                        className="product-thumb"
                                        onError={(e) => { e.target.src = missingImage; }}
                                    />
                                </td>
                                <td className="col-sku-cell">{item.sku}</td>
                                <td className="col-title-cell" title={item.title}>
                                    {(item.title || '').slice(0, 40)}{(item.title || '').length > 40 ? '...' : ''}
                                </td>
                                <td className="col-ai-cell">
                                    {renderAICell(item, 'title', item.aiTitle)}
                                </td>
                                <td className="col-ai-cell">
                                    {renderAICell(item, 'description', item.aiDescription)}
                                </td>
                                <td className="col-status-cell">
                                    {renderStatusBadge(item)}
                                </td>
                                <td className="col-actions-cell" onClick={(e) => e.stopPropagation()}>
                                    {item.enrichmentStatus === 'completed' && (
                                        <button
                                            className="btn btn-icon btn-sm text-success"
                                            title="Approve"
                                            onClick={() => {
                                                setSelectedProducts(new Set([item.id]));
                                                setTimeout(handleApprove, 0);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faCheck} />
                                        </button>
                                    )}
                                    {item.enrichmentStatus === 'approved' && (
                                        <button
                                            className="btn btn-icon btn-sm text-primary"
                                            title="Push to Shopify"
                                            onClick={() => {
                                                setSelectedProducts(new Set([item.id]));
                                                setTimeout(handlePushToShopify, 0);
                                            }}
                                        >
                                            🛒
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="8" className="empty-row">
                                    {hasActiveFilters ? (
                                        <div className="empty-state">
                                            <FontAwesomeIcon icon={faFilter} className="empty-icon" />
                                            <p>No products match your current filters</p>
                                            <button className="btn btn-link" onClick={clearFilters}>
                                                Clear all filters
                                            </button>
                                        </div>
                                    ) : (
                                        <p>No products found</p>
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="wizard-pagination">
                <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>First</button>
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 0}>Prev</button>
                <span>Page {currentPage + 1} of {totalPages || 1}</span>
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages - 1}>Next</button>
                <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1}>Last</button>
            </div>

            {/* Confirmation Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faRocket} className="me-2 text-success" />
                        Start AI Enrichment?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isOverBudget && (
                        <div className="alert alert-warning mb-3" role="alert">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>Over Budget:</strong> Estimated cost (${costEstimate.totalCost.toFixed(2)}) exceeds your budget threshold (${budget.threshold.toFixed(2)})
                        </div>
                    )}
                    {bypassSeoProtection && (
                        <div className="alert alert-info mb-3" role="alert">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>SEO Protection Override:</strong> Title, Description, and SEO modes will run even for products already in Shopify. This may affect existing search rankings.
                        </div>
                    )}
                    <div className="confirm-summary">
                        <div className="confirm-row">
                            <span>Products</span>
                            <strong>{selectedProducts.size}</strong>
                        </div>
                        <div className="confirm-row">
                            <span>Modes</span>
                            <strong>{selectedModes.map(m => MODE_CONFIG[m].label).join(', ')}</strong>
                        </div>
                        <div className="confirm-row">
                            <span>Estimated Cost</span>
                            <strong className={isOverBudget ? 'text-warning' : 'text-success'}>
                                ${costEstimate.totalCost.toFixed(2)}
                            </strong>
                        </div>
                        <div className="confirm-row">
                            <span>Estimated Time</span>
                            <strong>~{costEstimate.timeMinutes} minutes</strong>
                        </div>
                    </div>
                    <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
                        Once started, you'll see real-time progress in the grid. Processing cannot be cancelled.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button variant={isOverBudget ? 'warning' : 'success'} onClick={handleStartEnrichment} disabled={processing}>
                        {processing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Starting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faRocket} className="me-2" />
                                {isOverBudget ? 'Proceed Anyway' : 'Start Enrichment'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Budget Settings Modal */}
            <BudgetSettingsModal
                show={showBudgetSettings}
                onHide={() => setShowBudgetSettings(false)}
                currentBudget={budget}
                onSave={saveBudget}
            />
        </div>
    );
};

export default UnifiedEnrichmentWizard;
